import styled, { css } from 'styled-components';

import { getFontStyle, rem } from '../../utils';
import { TileProps } from './Tile';
import { TileBadgePosition } from './TileBadge';

export const buttonSize = 30;

export const Size: Record<string, { width: number; height?: number }> = {
  small: {
    width: 140,
    height: 78,
  },
  medium: {
    width: 140,
    height: 120,
  },
  full: {
    width: 140,
  },
};

export const getTileSize = ({
  variant = 'inline',
  size = 'medium',
}: {
  variant?: TileProps['variant'];
  size?: TileProps['size'];
}) => {
  const width = variant === 'full' ? '100%' : `${Size[size].width}px`;
  const height = size === 'full' ? 'auto' : `${Size[size].height}px`;

  return css`
    width: ${width};
    max-width: 100%;
    height: ${height};
    min-height: ${rem(60)};
  `;
};

export const StyledText = styled.span<{ label?: string }>`
  position: absolute;
  display: flex;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  ${getFontStyle('SubHeading')}
  line-height: ${rem(24)};
  color: ${({ theme }) => theme.Color.Overlay.Hover};
  background: ${({ theme, label }) => (label ? theme.Color.Overlay.Default : 'transparent')};
  padding: ${({ theme }) => rem(theme.Spacing.Size.MediumSmall)};
  align-items: center;
  justify-content: center;
  text-align: center;
  transition: all ${({ theme }) => theme.Transition.Timing.Out};
`;

export const StyledBadgesWrapper = styled.div<{
  visible: boolean;
  position?: TileBadgePosition;
}>`
  position: absolute;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  user-select: none;
  pointer-events: none;
  z-index: ${({ theme }) => theme.ZIndex.ZIndex.Elevated};
  transition: all ${({ theme }) => theme.Transition.Timing.Out};

  ${({ visible }) =>
    visible
      ? `
      visibility: visible;
      opacity: 1;
    `
      : `
      visibility: hidden;
      opacity: 0;
      pointer-events: none;
  `}

  ${({ theme, position }) => {
    const spacing = rem(theme.Spacing.Size.XXSmall);

    switch (position) {
      case 'top-right':
        return `
          top: 0;
          right: ${spacing};
        `;
      case 'bottom-left':
        return `
          bottom: 0;
          left: ${spacing};
        `;
      case 'bottom-right':
        return `
          bottom: 0;
          right: ${spacing};
        `;
      case 'top-left':
      default:
        return `
          top: 0;
          left: ${spacing};
        `;
    }
  }}
`;

export const StyledBadge = styled.a<{
  backgroundColor?: string;
  onClick?: () => void;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  width: ${rem(buttonSize)};
  min-width: ${rem(buttonSize)};
  height: ${rem(buttonSize)};
  padding: ${rem(3)};
  margin: ${({ theme }) => `${rem(theme.Spacing.Size.XSmall)} ${rem(theme.Spacing.Size.XXSmall)}`};
  background: ${({ theme, backgroundColor }) =>
    backgroundColor || theme.Color.OverlayFixed.Default};
  fill: ${({ theme }) => theme.Color.Neutrals.Paper};
  color: ${({ theme }) => theme.Color.Neutrals.Paper};
  border-radius: 1000px;
  text-align: right;
  transition: all ${({ theme }) => theme.Transition.Timing.In};

  ${({ onClick }) =>
    onClick
      ? `cursor: pointer;`
      : `
    user-select: none;
    pointer-events: none;
  `}
`;

export const StyledBadgeLabel = styled.span`
  position: absolute;
  visibility: none;
  right: ${({ theme }) => rem(theme.Spacing.Size.Small)};
  ${getFontStyle('Small')}
  text-transform: uppercase;
  color: ${({ theme }) => theme.Color.NeutralsFixed.Light};
  vertical-align: middle;
  margin-left: ${({ theme }) => rem(theme.Spacing.Size.XXSmall)};
  opacity: 0;
  transform: translateX(50%);
  transition: opacity ${({ theme }) => theme.Transition.Timing.Out},
    transform ${({ theme }) => theme.Transition.Timing.Out};
`;

export const StyledButton = styled.a<{
  backgroundColor?: string;
  onClick?: () => void;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(buttonSize)};
  height: ${rem(buttonSize)};
  margin-top: ${({ theme }) => rem(theme.Spacing.Size.XSmall)};
  margin-left: ${({ theme }) => rem(theme.Spacing.Size.XXSmall)};
  fill: ${({ theme }) => theme.Color.Neutrals.Paper};
  color: ${({ theme }) => theme.Color.Neutrals.Paper};
  border: 1.5px dashed #ffffff;
  border-radius: 2px;

  ${({ onClick }) =>
    onClick
      ? `cursor: pointer;`
      : `
    user-select: none;
    pointer-events: none;
  `}
`;

export const StyledTile = styled.a<{
  style: object;
  backgroundImage: TileProps['imageUrl'];
  title?: TileProps['label'];
  size: TileProps['size'];
}>`
  position: absolute;
  display: block;
  width: 100%;
  height: 100%;
  overflow: hidden;
  flex-shrink: 0;
  cursor: pointer;
  user-select: none;
  background-color: ${({ theme }) => theme.Color.Neutrals.Light};
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  border: 1px solid ${({ theme }) => theme.Color.Neutrals.Light};
  border-radius: ${({ theme }) => theme.Border.Radius.Medium}px;
`;

export const StyledWrapper = styled.div<{
  variant: TileProps['variant'];
  title?: TileProps['label'];
  size: TileProps['size'];
}>`
  position: relative;
  ${({ variant, size }) => getTileSize({ variant, size })}
  cursor: pointer;

  &:hover,
  &:focus,
  &:active {
    ${StyledText} {
      background: ${({ theme, title }) =>
        title ? theme.Color.OverlayFixed.Hover : theme.Color.OverlayFixed.Default};
      transition: all ${({ theme }) => theme.Transition.Timing.In};
    }

    ${StyledBadge}:has(span) {
      width: ${rem(70)};
      transition: padding-left ${({ theme }) => theme.Transition.Timing.In},
        width ${({ theme }) => theme.Transition.Timing.In};
    }

    ${StyledBadgeLabel} {
      visibility: visible;
      opacity: 1;
      transform: translateX(0);
      transition: opacity ${({ theme }) => theme.Transition.Timing.In},
        transform ${({ theme }) => theme.Transition.Timing.In};
    }
  }
`;
